import { Control, FieldErrors } from 'react-hook-form';
import { DarwinDivider } from 'components/darwin-ui';
import { DataCaptureEditFormData } from 'types/dataCaptureTypes';
import { EditDataCaptureFormSectionField } from './EditDataCaptureFormSectionField';
import { TransformedSection } from './utils';
import { EditableText } from './EditableText';
import styles from './DataCapture.module.css';
import { useTranslation } from 'react-i18next';
import { translateField } from 'utils/common';

type EditDataCaptureFormSectionProps = {
  section: TransformedSection;
  sectionIndex: number;
  control: Control<DataCaptureEditFormData>;
  errors: FieldErrors<DataCaptureEditFormData>;
  isViewForm: boolean;
};

export const EditDataCaptureFormSection = ({
  section,
  sectionIndex,
  control,
  errors,
  isViewForm,
}: EditDataCaptureFormSectionProps) => {
  const { t } = useTranslation();
  // For the first section, override the fields layout
  if (sectionIndex === 0) {
    return (
      <div className={styles.sectionContainer}>
        {/* Section title */}
        <div className={styles.sectionTitleContainer}>
          <EditableText
            isViewForm={isViewForm}
            fieldName={`sections.${sectionIndex}.section_name`}
            textClassnames={styles.sectionTitle}
            defaultText={translateField(t, section.section_name, {
              by: 'section-name',
            })}
          />
          <EditableText
            isViewForm={isViewForm}
            fieldName={`sections.${sectionIndex}.section_help_text`}
            textClassnames={styles.sectionTitle}
            isTooltip
          />
        </div>
        <DarwinDivider className={styles.titleDivider} />

        {/* First row(s) rendered normally (fields 0–3) */}
        <div className={styles.formSectionFieldsContainer}>
          {section.fields.slice(0, 4).map((field, index) => (
            <EditDataCaptureFormSectionField
              key={field.form_field_id}
              field={field}
              isVisible={field.is_visible}
              fieldIndex={index}
              sectionIndex={sectionIndex}
              control={control}
              errors={errors}
              isViewForm={isViewForm}
              defaultText={translateField(t, field.field_key, {
                subKey: 'field_name',
                by: 'key',
              })}
              defaultTooltipText={translateField(t, field.field_key, {
                subKey: 'help_text',
                by: 'key',
              })}
            />
          ))}
        </div>

        {/* Third row: fields 4 and 5 wrapped in a nested container */}
        <div className={styles.nestedRowContainer}>
          <div className={styles.nestedGrid}>
            {section.fields.slice(4, 6).map((field, index) => (
              <EditDataCaptureFormSectionField
                key={field.form_field_id}
                field={field}
                isVisible={field.is_visible}
                fieldIndex={4 + index}
                sectionIndex={sectionIndex}
                control={control}
                errors={errors}
                isViewForm={isViewForm}
                defaultText={translateField(t, field.field_key, {
                  subKey: 'field_name',
                  by: 'key',
                })}
                defaultTooltipText={translateField(t, field.field_key, {
                  subKey: 'help_text',
                  by: 'key',
                })}
              />
            ))}
          </div>
        </div>
      </div>
    );
  }

  // For all other sections, keep the original layout
  return (
    <div className={styles.sectionContainer}>
      <div className={styles.sectionTitleContainer}>
        <EditableText
          isViewForm={isViewForm}
          fieldName={`sections.${sectionIndex}.section_name`}
          textClassnames={styles.sectionTitle}
          defaultText={translateField(t, section.section_name, {
            by: 'section-name',
          })}
        />
        <EditableText
          isViewForm={isViewForm}
          fieldName={`sections.${sectionIndex}.section_help_text`}
          textClassnames={styles.sectionTitle}
          isTooltip
        />
      </div>
      <DarwinDivider className={styles.titleDivider} />
      <div className={styles.formSectionFieldsContainer}>
        {section.fields.map((field, index) => (
          <EditDataCaptureFormSectionField
            key={field.form_field_id}
            field={field}
            isVisible={field.is_visible}
            fieldIndex={index}
            sectionIndex={sectionIndex}
            control={control}
            errors={errors}
            isViewForm={isViewForm}
            defaultText={
              !field.is_custom
                ? translateField(t, field.field_key, {
                    subKey: 'field_name',
                    by: 'key',
                  })
                : field.field_name
            }
            defaultTooltipText={
              !field.is_custom
                ? translateField(t, field.field_key, {
                    subKey: 'help_text',
                    by: 'key',
                  })
                : field.help_text
            }
          />
        ))}
      </div>
    </div>
  );
};
